import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { Plus, Trash2, TrendingUp, Zap, Pencil, Image } from "lucide-react";
import { Modal } from "react-bootstrap";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "../loader";
import { useAlert } from "react-alert";
import api from "../../../api";
import "../AddPost.css";
import "./Live.css";
import "../AdminMarket.css";

const INITIAL_SPOT = {
  exchange: "",
  coin: "",
  entryPrice: "",
  takeProfit: "",
  stopLoss: "",
  filledAmountUSDT: "",
  filledQuantityCoin: "",
  status: "Open",
};

const INITIAL_FUTURES = {
  exchange: "",
  coin: "",
  positionType: "",
  leverage: "",
  marginMode: "",
  entryPrice: "",
  stopLoss: "",
  takeProfit: "",
  marginUsedUSDT: "",
  positionSizeUSDT: "",
  liquidationPrice: "",
  status: "Open",
  pnlUSDT: "",
  roiPercent: "",
};

function LiveSessionTrade() {
  const alert = useAlert();
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [trades, setTrades] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loader, setLoader] = useState(true);
  const [submitLoader, setSubmitLoader] = useState(false);

  const [show, setShow] = useState(false);
  const [tradeType, setTradeType] = useState(null);
  const [spotTrade, setSpotTrade] = useState(INITIAL_SPOT);
  const [futuresTrade, setFuturesTrade] = useState(INITIAL_FUTURES);
  const [description, setDescription] = useState("");
  const [imageTitle, setImageTitle] = useState("");
  const [imageFormat, setImageFormat] = useState("");
  const [selectedImg, setSelectedImg] = useState([]);
  const [imgUploadLoader, setImgUploadLoader] = useState(false);
  const [error, setError] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTradeType, setEditTradeType] = useState(null);
  const [editSpotTrade, setEditSpotTrade] = useState(INITIAL_SPOT);
  const [editFuturesTrade, setEditFuturesTrade] = useState(INITIAL_FUTURES);
  const [editDescription, setEditDescription] = useState("");
  const [editImageTitle, setEditImageTitle] = useState("");
  const [editImageFormat, setEditImageFormat] = useState("");
  const [editSelectedImg, setEditSelectedImg] = useState([]);
  const [editImgUploadLoader, setEditImgUploadLoader] = useState(false);
  const [editError, setEditError] = useState("");
  const [editLoader, setEditLoader] = useState(false);

  const [previewImg, setPreviewImg] = useState(null);

  const fetchTrades = async (p = 1) => {
    try {
      setLoader(true);
      const res = await api.liveSessionTradeGetAdmin({ page: p });
      if (res?.success) {
        setTrades(res.trades);
        setTotalPages(Math.ceil(res.count / res.limit) || 1);
      }
    } catch (e) {
      console.error(e.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { fetchTrades(page); }, [page]);

  const handleSpotChange = (e) =>
    setSpotTrade({ ...spotTrade, [e.target.name]: e.target.value });

  const handleFuturesChange = (e) =>
    setFuturesTrade({ ...futuresTrade, [e.target.name]: e.target.value });

  const handleTradeTypeChange = (type) =>
    setTradeType(type === tradeType ? null : type);

  const onFileChange = async (e) => {
    try {
      if (!e.target.files?.length) return;
      setImgUploadLoader(true);
      const formData = new FormData();
      for (let file of e.target.files) formData.append("image", file);
      const uploadRes = await api.uploadFile(formData);
      const imgNames = uploadRes[0].split(",");
      const imgFormats = uploadRes[1].split(",");
      const previews = imgNames.map((n, i) => `${process.env.REACT_APP_S3URL}/img_${n}.${imgFormats[i]}`);
      setSelectedImg(previews);
      setImageTitle(uploadRes[0]);
      setImageFormat(uploadRes[1]);
    } catch {
      alert.error("Image upload failed");
    } finally {
      setImgUploadLoader(false);
    }
  };

  const onEditFileChange = async (e) => {
    try {
      if (!e.target.files?.length) return;
      setEditImgUploadLoader(true);
      const formData = new FormData();
      for (let file of e.target.files) formData.append("image", file);
      const uploadRes = await api.uploadFile(formData);
      const imgNames = uploadRes[0].split(",");
      const imgFormats = uploadRes[1].split(",");
      const previews = imgNames.map((n, i) => `${process.env.REACT_APP_S3URL}/img_${n}.${imgFormats[i]}`);
      setEditSelectedImg(previews);
      setEditImageTitle(uploadRes[0]);
      setEditImageFormat(uploadRes[1]);
    } catch {
      alert.error("Image upload failed");
    } finally {
      setEditImgUploadLoader(false);
    }
  };

  const resetForm = () => {
    setTradeType(null);
    setSpotTrade(INITIAL_SPOT);
    setFuturesTrade(INITIAL_FUTURES);
    setDescription("");
    setImageTitle("");
    setImageFormat("");
    setSelectedImg([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setError("");
  };

  const handleClose = () => { resetForm(); setShow(false); };

  const validate = () => {
    if (!tradeType && !description && !imageTitle) {
      setError("Please add a description, upload an image, or select a trade type.");
      return false;
    }
    if (tradeType === "spot") {
      const req = ["coin", "entryPrice", "takeProfit", "filledAmountUSDT", "filledQuantityCoin"];
      const missing = req.filter((f) => !spotTrade[f]);
      if (missing.length) { setError(`Missing: ${missing.join(", ")}`); return false; }
    }
    if (tradeType === "futures") {
      const req = ["coin", "positionType", "entryPrice", "stopLoss", "takeProfit"];
      const missing = req.filter((f) => !futuresTrade[f]);
      if (missing.length) { setError(`Missing: ${missing.join(", ")}`); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitLoader(true);
    setError("");
    try {
      const res = await api.liveSessionTradeAdd({
        tradeType: tradeType || null,
        spotTrade: tradeType === "spot" ? spotTrade : null,
        futuresTrade: tradeType === "futures" ? futuresTrade : null,
        description,
        imageTitle,
        imageFormat,
      });
      if (res?.success) {
        handleClose();
        fetchTrades(1);
        setPage(1);
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this trade?")) return;
    try {
      await api.liveSessionTradeDelete({ _id: id });
      fetchTrades(page);
    } catch (e) {
      console.error(e.message);
    }
  };

  const handleEditOpen = (t) => {
    setEditId(t._id);
    setEditTradeType(t.tradeType || null);
    if (t.tradeType === "spot") {
      setEditSpotTrade({ ...INITIAL_SPOT, ...t.spotTrade });
      setEditFuturesTrade(INITIAL_FUTURES);
    } else if (t.tradeType === "futures") {
      setEditFuturesTrade({ ...INITIAL_FUTURES, ...t.futuresTrade });
      setEditSpotTrade(INITIAL_SPOT);
    } else {
      setEditSpotTrade(INITIAL_SPOT);
      setEditFuturesTrade(INITIAL_FUTURES);
    }
    setEditDescription(t.description || "");
    setEditImageTitle(t.imageTitle || "");
    setEditImageFormat(t.imageFormat || "");
    if (t.imageTitle && t.imageFormat) {
      const names = t.imageTitle.split(",");
      const formats = t.imageFormat.split(",");
      setEditSelectedImg(names.map((n, i) => `${process.env.REACT_APP_S3URL}/img_${n}.${formats[i]}`));
    } else {
      setEditSelectedImg([]);
    }
    setEditError("");
    setShowEdit(true);
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setEditId(null);
    setEditTradeType(null);
    setEditSpotTrade(INITIAL_SPOT);
    setEditFuturesTrade(INITIAL_FUTURES);
    setEditDescription("");
    setEditImageTitle("");
    setEditImageFormat("");
    setEditSelectedImg([]);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
    setEditError("");
  };

  const handleEditSubmit = async () => {
    if (!editTradeType && !editDescription && !editImageTitle) {
      setEditError("Please add a description, upload an image, or select a trade type.");
      return;
    }
    setEditLoader(true);
    setEditError("");
    try {
      const res = await api.liveSessionTradeEdit({
        _id: editId,
        tradeType: editTradeType || null,
        spotTrade: editTradeType === "spot" ? editSpotTrade : null,
        futuresTrade: editTradeType === "futures" ? editFuturesTrade : null,
        description: editDescription,
        imageTitle: editImageTitle,
        imageFormat: editImageFormat,
      });
      if (res?.success) {
        handleEditClose();
        fetchTrades(page);
      }
    } catch (e) {
      setEditError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setEditLoader(false);
    }
  };

  const tradeData = (t) => t.tradeType === "spot" ? t.spotTrade : t.futuresTrade;

  const statusClass = (s) => {
    if (s === "Open") return "badge bg-success";
    if (s === "TP Hit") return "badge bg-primary";
    if (s === "SL Hit") return "badge bg-danger";
    return "badge bg-secondary";
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center"
            style={{ width: 56, height: 56, background: "linear-gradient(135deg,#7c3aed,#a855f7)" }}
          >
            <TrendingUp size={26} color="#fff" />
          </div>
          <div>
            <h4 className="mb-0 fw-700">Live Session Trades</h4>
            <span className="badge" style={{ background: "#7c3aed", fontSize: 12 }}>
              {trades.length} trade{trades.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <button
          className="btn text-white fw-600 d-flex align-items-center gap-2"
          style={{ background: "#7c3aed", borderRadius: 10 }}
          onClick={() => setShow(true)}
        >
          <Plus size={18} /> Add Trade
        </button>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-body p-0">
          {loader ? (
            <div className="py-5"><Loader /></div>
          ) : trades.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <TrendingUp size={40} className="mb-3 opacity-25" />
              <p className="mb-0">No trades yet. Click <strong>Add Trade</strong> to get started.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#f8f7ff" }}>
                  <tr>
                    <th className="ps-4 py-3 text-muted fw-600" style={{ fontSize: 13 }}>#</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>TYPE</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>COIN</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>ENTRY</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>TP</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>SL</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>STATUS</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>DESCRIPTION</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>IMAGE</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>CREATED AT</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => {
                    const d = tradeData(t);
                    return (
                      <tr key={t._id}>
                        <td className="ps-4 text-muted" style={{ fontSize: 13 }}>
                          {(page - 1) * 10 + i + 1}
                        </td>
                        <td>
                          {t.tradeType ? (
                            <span
                              className="badge"
                              style={{
                                background: t.tradeType === "spot" ? "#d1fae5" : "#ede9fe",
                                color: t.tradeType === "spot" ? "#065f46" : "#5b21b6",
                                fontSize: 12,
                              }}
                            >
                              {t.tradeType === "spot"
                                ? <><TrendingUp size={11} className="me-1" />Spot</>
                                : <><Zap size={11} className="me-1" />Futures</>}
                            </span>
                          ) : (
                            <span className="badge" style={{ background: "#f3f4f6", color: "#6b7280", fontSize: 11 }}>Update</span>
                          )}
                        </td>
                        <td className="fw-600">{d?.coin || "-"}</td>
                        <td>{d?.entryPrice ?? "-"}</td>
                        <td>{d?.takeProfit ?? "-"}</td>
                        <td>{d?.stopLoss ?? "-"}</td>
                        <td>
                          <span className={statusClass(d?.status)} style={{ fontSize: 12 }}>
                            {d?.status}
                          </span>
                        </td>
                        <td style={{ maxWidth: 180 }}>
                          {t.description ? (
                            <span
                              className="text-muted"
                              style={{ fontSize: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                              title={t.description}
                            >
                              {t.description}
                            </span>
                          ) : (
                            <span className="text-muted" style={{ fontSize: 12 }}>-</span>
                          )}
                        </td>
                        <td>
                          {t.imageTitle && t.imageFormat ? (() => {
                            const names = t.imageTitle.split(",");
                            const formats = t.imageFormat.split(",");
                            return (
                              <div className="d-flex gap-1 flex-wrap">
                                {names.map((n, idx) => (
                                  <img
                                    key={idx}
                                    src={`${process.env.REACT_APP_S3URL}/img_${n}.${formats[idx]}`}
                                    alt="img"
                                    style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer" }}
                                    onClick={() => setPreviewImg(`${process.env.REACT_APP_S3URL}/img_${n}.${formats[idx]}`)}
                                  />
                                ))}
                              </div>
                            );
                          })() : (
                            <span className="text-muted" style={{ fontSize: 12 }}>-</span>
                          )}
                        </td>
                        <td className="text-muted" style={{ fontSize: 13 }}>
                          {t.createdAt ? moment(t.createdAt).format("MMM D, YYYY h:mm A") : "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleEditOpen(t)}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(t._id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              color="primary"
            />
          </Stack>
        </div>
      )}

      {/* Edit Trade Modal */}
      <Modal show={showEdit} onHide={handleEditClose} size="xl" centered className="modern-modal">
        <Modal.Body className="modern-modal-body">
          <div className="modal-header-section">
            <h3 className="modal-title">Edit Live Session Trade</h3>
            <button className="modal-close-btn" onClick={handleEditClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-content-section">
            {editError && (
              <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
                {editError}
              </div>
            )}

            {/* Description */}
            <div className="mb-3">
              <label className="trade-label">Description <span className="text-muted" style={{ fontSize: 12 }}>(optional)</span></label>
              <textarea
                className="trade-input w-100"
                rows={3}
                placeholder="Add a description or update note..."
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                style={{ resize: "vertical", minHeight: 70 }}
              />
            </div>

            {/* Image Upload */}
            <div className="mb-3">
              <label className="trade-label">Image <span className="text-muted" style={{ fontSize: 12 }}>(optional)</span></label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onEditFileChange}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                  onClick={() => editFileInputRef.current?.click()}
                  disabled={editImgUploadLoader}
                >
                  <Image size={14} />
                  {editImgUploadLoader ? "Uploading..." : editSelectedImg.length ? "Change Image" : "Upload Image"}
                </button>
                {editSelectedImg.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-1">
                    {editSelectedImg.map((src, i) => (
                      <img key={i} src={src} alt="preview" style={{ height: 60, borderRadius: 6, objectFit: "cover", border: "1px solid #e5e7eb" }} />
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => { setEditSelectedImg([]); setEditImageTitle(""); setEditImageFormat(""); if (editFileInputRef.current) editFileInputRef.current.value = ""; }}
                    >Remove</button>
                  </div>
                )}
              </div>
            </div>

            {editTradeType === "spot" && (
              <div className="trade-fields-container trade-fields-spot">
                <div className="trade-fields-badge"><i className="fas fa-coins"></i> Spot Trade</div>
                <div className="trade-grid">
                  <div className="trade-field">
                    <label className="trade-label">Exchange</label>
                    <input type="text" name="exchange" className="trade-input"
                      placeholder="e.g. Binance" value={editSpotTrade.exchange}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Coin <span className="required-star">*</span></label>
                    <input type="text" name="coin" className="trade-input"
                      placeholder="e.g. BTC" value={editSpotTrade.coin}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Entry Price – Buy Limit <span className="required-star">*</span></label>
                    <input type="number" name="entryPrice" className="trade-input"
                      value={editSpotTrade.entryPrice}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Take Profit – Sell Limit <span className="required-star">*</span></label>
                    <input type="number" name="takeProfit" className="trade-input"
                      value={editSpotTrade.takeProfit}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Stop Loss (SL)</label>
                    <input type="number" name="stopLoss" className="trade-input"
                      value={editSpotTrade.stopLoss}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Filled Amount (USDT) <span className="required-star">*</span></label>
                    <input type="number" name="filledAmountUSDT" className="trade-input"
                      value={editSpotTrade.filledAmountUSDT}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Filled Quantity (COIN) <span className="required-star">*</span></label>
                    <input type="number" name="filledQuantityCoin" className="trade-input"
                      value={editSpotTrade.filledQuantityCoin}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Status <span className="required-star">*</span></label>
                    <select name="status" className="trade-input trade-select"
                      value={editSpotTrade.status}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })}>
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="TP Hit">TP Hit</option>
                      <option value="SL Hit">SL Hit</option>
                    </select>
                  </div>
                </div>
                <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
              </div>
            )}

            {editTradeType === "futures" && (
              <div className="trade-fields-container trade-fields-futures">
                <div className="trade-fields-badge futures-badge"><i className="fas fa-bolt"></i> Futures Trade</div>
                <div className="trade-grid">
                  <div className="trade-field">
                    <label className="trade-label">Exchange</label>
                    <input type="text" name="exchange" className="trade-input"
                      value={editFuturesTrade.exchange}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Coin <span className="required-star">*</span></label>
                    <input type="text" name="coin" className="trade-input"
                      value={editFuturesTrade.coin}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Position Type <span className="required-star">*</span></label>
                    <select name="positionType" className="trade-input trade-select"
                      value={editFuturesTrade.positionType}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })}>
                      <option value="">Select</option>
                      <option value="Long">Long</option>
                      <option value="Short">Short</option>
                    </select>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Leverage</label>
                    <div className="trade-input-suffix">
                      <input type="number" name="leverage" className="trade-input"
                        value={editFuturesTrade.leverage}
                        onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                      <span className="suffix-label">x</span>
                    </div>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Margin Mode</label>
                    <select name="marginMode" className="trade-input trade-select"
                      value={editFuturesTrade.marginMode}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })}>
                      <option value="">Select</option>
                      <option value="Cross">Cross</option>
                      <option value="Isolated">Isolated</option>
                    </select>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Entry Price <span className="required-star">*</span></label>
                    <input type="number" name="entryPrice" className="trade-input"
                      value={editFuturesTrade.entryPrice}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Stop Loss (SL) <span className="required-star">*</span></label>
                    <input type="number" name="stopLoss" className="trade-input"
                      value={editFuturesTrade.stopLoss}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Take Profit (TP) <span className="required-star">*</span></label>
                    <input type="number" name="takeProfit" className="trade-input"
                      value={editFuturesTrade.takeProfit}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Margin Used (USDT)</label>
                    <input type="number" name="marginUsedUSDT" className="trade-input"
                      value={editFuturesTrade.marginUsedUSDT}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Position Size (USDT)</label>
                    <input type="number" name="positionSizeUSDT" className="trade-input"
                      value={editFuturesTrade.positionSizeUSDT}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Liquidation Price</label>
                    <input type="number" name="liquidationPrice" className="trade-input"
                      value={editFuturesTrade.liquidationPrice}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Status <span className="required-star">*</span></label>
                    <select name="status" className="trade-input trade-select"
                      value={editFuturesTrade.status}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })}>
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="TP Hit">TP Hit</option>
                      <option value="SL Hit">SL Hit</option>
                    </select>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">PNL (USDT)</label>
                    <input type="number" name="pnlUSDT" className="trade-input"
                      value={editFuturesTrade.pnlUSDT}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">ROI (%)</label>
                    <div className="trade-input-suffix">
                      <input type="number" name="roiPercent" className="trade-input"
                        value={editFuturesTrade.roiPercent}
                        onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                      <span className="suffix-label">%</span>
                    </div>
                  </div>
                </div>
                <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-light fw-600 px-4" onClick={handleEditClose}>Cancel</button>
              <button
                className="btn text-white fw-600 px-4"
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}
                onClick={handleEditSubmit}
                disabled={editLoader}
              >
                {editLoader ? "Saving..." : "Update Trade"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Trade Modal */}
      <Modal show={show} onHide={handleClose} size="xl" centered className="modern-modal">
        <Modal.Body className="modern-modal-body">
          <div className="modal-header-section">
            <h3 className="modal-title">Add Live Session Trade</h3>
            <button className="modal-close-btn" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-content-section">
          {error && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Description */}
          <div className="mb-3">
            <label className="trade-label">Description <span className="text-muted" style={{ fontSize: 12 }}>(optional)</span></label>
            <textarea
              className="trade-input w-100"
              rows={3}
              placeholder="Add a description or update note..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ resize: "vertical", minHeight: 70 }}
            />
          </div>

          {/* Image Upload */}
          <div className="mb-3">
            <label className="trade-label">Image <span className="text-muted" style={{ fontSize: 12 }}>(optional)</span></label>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onFileChange}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                onClick={() => fileInputRef.current?.click()}
                disabled={imgUploadLoader}
              >
                <Image size={14} />
                {imgUploadLoader ? "Uploading..." : selectedImg.length ? "Change Image" : "Upload Image"}
              </button>
              {selectedImg.length > 0 && (
                <div className="d-flex gap-2 flex-wrap mt-1">
                  {selectedImg.map((src, i) => (
                    <img key={i} src={src} alt="preview" style={{ height: 60, borderRadius: 6, objectFit: "cover", border: "1px solid #e5e7eb" }} />
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => { setSelectedImg([]); setImageTitle(""); setImageFormat(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  >Remove</button>
                </div>
              )}
            </div>
          </div>

          {/* Trade Type Toggle */}
          <div className="trade-type-toggle mb-3">
            <button
              type="button"
              className={`trade-type-btn ${tradeType === "spot" ? "active-spot" : ""}`}
              onClick={() => handleTradeTypeChange("spot")}
            >
              <i className="fas fa-coins me-2"></i>Spot Trade
            </button>
            <button
              type="button"
              className={`trade-type-btn ${tradeType === "futures" ? "active-futures" : ""}`}
              onClick={() => handleTradeTypeChange("futures")}
            >
              <i className="fas fa-bolt me-2"></i>Futures Trade
            </button>
            {tradeType && (
              <button type="button" className="trade-type-btn trade-type-clear" onClick={() => setTradeType(null)}>
                <i className="fas fa-times me-2"></i>Clear
              </button>
            )}
          </div>

          {/* Spot Fields */}
          {tradeType === "spot" && (
            <div className="trade-fields-container trade-fields-spot">
              <div className="trade-fields-badge"><i className="fas fa-coins"></i> Spot Trade</div>
              <div className="trade-grid">
                <div className="trade-field">
                  <label className="trade-label">Exchange</label>
                  <input type="text" name="exchange" className="trade-input"
                    placeholder="e.g. Binance" value={spotTrade.exchange} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Coin <span className="required-star">*</span></label>
                  <input type="text" name="coin" className="trade-input"
                    placeholder="e.g. BTC" value={spotTrade.coin} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Entry Price – Buy Limit <span className="required-star">*</span></label>
                  <input type="number" name="entryPrice" className="trade-input"
                    placeholder="e.g. 67500" value={spotTrade.entryPrice} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Take Profit – Sell Limit <span className="required-star">*</span></label>
                  <input type="number" name="takeProfit" className="trade-input"
                    placeholder="e.g. 72000" value={spotTrade.takeProfit} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Stop Loss (SL)</label>
                  <input type="number" name="stopLoss" className="trade-input"
                    placeholder="e.g. 63000 (optional)" value={spotTrade.stopLoss} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Filled Amount (USDT) <span className="required-star">*</span></label>
                  <input type="number" name="filledAmountUSDT" className="trade-input"
                    placeholder="e.g. 1000" value={spotTrade.filledAmountUSDT} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Filled Quantity (COIN) <span className="required-star">*</span></label>
                  <input type="number" name="filledQuantityCoin" className="trade-input"
                    placeholder="e.g. 0.01481" value={spotTrade.filledQuantityCoin} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Status <span className="required-star">*</span></label>
                  <select name="status" className="trade-input trade-select"
                    value={spotTrade.status} onChange={handleSpotChange}>
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                    <option value="TP Hit">TP Hit</option>
                    <option value="SL Hit">SL Hit</option>
                  </select>
                </div>
              </div>
              <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
            </div>
          )}

          {/* Futures Fields */}
          {tradeType === "futures" && (
            <div className="trade-fields-container trade-fields-futures">
              <div className="trade-fields-badge futures-badge"><i className="fas fa-bolt"></i> Futures Trade</div>
              <div className="trade-grid">
                <div className="trade-field">
                  <label className="trade-label">Exchange</label>
                  <input type="text" name="exchange" className="trade-input"
                    placeholder="e.g. Bybit" value={futuresTrade.exchange} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Coin <span className="required-star">*</span></label>
                  <input type="text" name="coin" className="trade-input"
                    placeholder="e.g. ETH" value={futuresTrade.coin} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Position Type <span className="required-star">*</span></label>
                  <select name="positionType" className="trade-input trade-select"
                    value={futuresTrade.positionType} onChange={handleFuturesChange}>
                    <option value="">Select</option>
                    <option value="Long">Long</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="trade-field">
                  <label className="trade-label">Leverage</label>
                  <div className="trade-input-suffix">
                    <input type="number" name="leverage" className="trade-input"
                      placeholder="e.g. 10" value={futuresTrade.leverage} onChange={handleFuturesChange} />
                    <span className="suffix-label">x</span>
                  </div>
                </div>
                <div className="trade-field">
                  <label className="trade-label">Margin Mode</label>
                  <select name="marginMode" className="trade-input trade-select"
                    value={futuresTrade.marginMode} onChange={handleFuturesChange}>
                    <option value="">Select</option>
                    <option value="Cross">Cross</option>
                    <option value="Isolated">Isolated</option>
                  </select>
                </div>
                <div className="trade-field">
                  <label className="trade-label">Entry Price <span className="required-star">*</span></label>
                  <input type="number" name="entryPrice" className="trade-input"
                    placeholder="e.g. 3200" value={futuresTrade.entryPrice} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Stop Loss (SL) <span className="required-star">*</span></label>
                  <input type="number" name="stopLoss" className="trade-input"
                    placeholder="e.g. 3050" value={futuresTrade.stopLoss} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Take Profit (TP) <span className="required-star">*</span></label>
                  <input type="number" name="takeProfit" className="trade-input"
                    placeholder="e.g. 3600" value={futuresTrade.takeProfit} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Margin Used (USDT)</label>
                  <input type="number" name="marginUsedUSDT" className="trade-input"
                    placeholder="e.g. 500" value={futuresTrade.marginUsedUSDT} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Position Size (USDT)</label>
                  <input type="number" name="positionSizeUSDT" className="trade-input"
                    placeholder="e.g. 5000" value={futuresTrade.positionSizeUSDT} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Liquidation Price</label>
                  <input type="number" name="liquidationPrice" className="trade-input"
                    placeholder="e.g. 2900" value={futuresTrade.liquidationPrice} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Status <span className="required-star">*</span></label>
                  <select name="status" className="trade-input trade-select"
                    value={futuresTrade.status} onChange={handleFuturesChange}>
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                    <option value="TP Hit">TP Hit</option>
                    <option value="SL Hit">SL Hit</option>
                  </select>
                </div>
                <div className="trade-field">
                  <label className="trade-label">PNL (USDT)</label>
                  <input type="number" name="pnlUSDT" className="trade-input"
                    placeholder="e.g. 320" value={futuresTrade.pnlUSDT} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">ROI (%)</label>
                  <div className="trade-input-suffix">
                    <input type="number" name="roiPercent" className="trade-input"
                      placeholder="e.g. 6.4" value={futuresTrade.roiPercent} onChange={handleFuturesChange} />
                    <span className="suffix-label">%</span>
                  </div>
                </div>
              </div>
              <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-light fw-600 px-4" onClick={handleClose}>Cancel</button>
            <button
              className="btn text-white fw-600 px-4"
              style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}
              onClick={handleSubmit}
              disabled={submitLoader}
            >
              {submitLoader ? "Saving..." : "Save Trade"}
            </button>
          </div>
        </div>{/* end modal-content-section */}
        </Modal.Body>
      </Modal>

      {/* Image Preview Modal */}
      <Modal show={!!previewImg} onHide={() => setPreviewImg(null)} centered size="lg">
        <Modal.Body className="p-0 position-relative" style={{ background: "#000", borderRadius: 8 }}>
          <button
            onClick={() => setPreviewImg(null)}
            style={{
              position: "absolute", top: 10, right: 10, zIndex: 10,
              background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%",
              width: 32, height: 32, color: "#fff", cursor: "pointer", fontSize: 16,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ×
          </button>
          {previewImg && (
            <img
              src={previewImg}
              alt="preview"
              style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 8, display: "block" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default LiveSessionTrade;
